import { useCallback, useEffect, useRef, useState } from 'react'
import { AppButton } from '@/shared/ui/button'

import { useUnit } from 'effector-react'

import { lang } from '@/shared/constants/lang'
import { useStyles } from './style'

import { goToAuth, goToShop } from '@/app/routing/events'
import { $userLanguage, $userToken } from '@/entities/user'
import { RewardRarity } from '@/entities/cases'

import { getCaseListFx, $casesList, $isOpenedFree, $lastRewardFromCase, $lastViewedCaseId } from '@/entities/cases'
import { useHideNav } from '@/shared/hooks/useHideNav'
import { Icon, TIconVariant } from '@/shared/ui/icon'
import Ringgy from '@/features/ringgy'
import { useOpenCase } from '@/shared/hooks/useOpenCase'
import { formatNumber } from '@/shared/util/formatNumber'
import { rewardRarityObj } from './types'
import { RewardType } from '@/entities/cases'
import { convertMonts } from '@/shared/util/convertMonths'
import { Spiner } from '@/shared/ui/spiner'
import { MemoizedVideo } from './video'

export const RewardClaim = () => {
  useHideNav()
  const userLang = useUnit($userLanguage)
  const userToken = useUnit($userToken)

  const { classes } = useStyles()

  const lastViewedCaseId = useUnit($lastViewedCaseId)

  const cases = useUnit($casesList)
  const caseInfo = cases?.find((c) => c.id === lastViewedCaseId)
  const lastOpenedReward = useUnit($lastRewardFromCase)
  const reward = lastOpenedReward.reward
  const isUniqueReward = reward && reward.kind === 0

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [isVideoEnded, setVideoEnded] = useState(false)
  const [playbackRate, setPlaybackRate] = useState<number>(1)

  const isOpenedFree = useUnit($isOpenedFree)

  const { handleOpenCase } = useOpenCase()
  const btnTexts = lang[userLang].pages.btnTexts

  useEffect(() => {
    if (!cases && userToken) getCaseListFx({ token: userToken })
  }, [cases, userToken])
  useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.playbackRate = playbackRate
    }
  }, [playbackRate])

  const handleTapRing = useCallback((tapCount: number) => {
    if (tapCount <= 1) {
      setPlaybackRate(1)
    } else if (tapCount <= 3) {
      setPlaybackRate(2)
    } else if (tapCount <= 5) {
      setPlaybackRate(3)
    } else {
      setPlaybackRate(5)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      video.addEventListener('ended', () => setVideoEnded(true))
    }

    return () => {
      if (video) {
        video.removeEventListener('ended', () => setVideoEnded(true))
      }
    }
  }, [])

  if (!cases || !caseInfo || !reward) return <Spiner size="m" />

  return (
    <>
      <>
        <Ringgy isEnabled={!isVideoEnded} onTap={handleTapRing} />
        <MemoizedVideo caseName={caseInfo?.name ?? 'gamed'} videoRef={videoRef} />
      </>

      <div
        className={classes.root}
        data-isvideoended={isVideoEnded}
        data-rarity={rewardRarityObj[reward!.rarity as keyof typeof rewardRarityObj][0]}
      >
        <div className={classes.headingWrapper}>
          <span className={classes.heading}>{lang[userLang].pages.boxPage.widgetTexts.rewardClaim.heading}</span>
        </div>
        <div className={classes.rewardInfoWrapper} data-isunique={isUniqueReward}>
          <div className={classes.imageWrapper} data-isunique={isUniqueReward}>
            <img src={reward.photo_url} alt="" className={classes.img} />
          </div>
          <RenderRewardContent reward={reward} />
        </div>
        <div className={classes.buttonsWrapper}>
          <AppButton
            data-free={isOpenedFree}
            onClick={() => {
              if (userToken) {
                getCaseListFx({ token: userToken })
                goToShop({ tab: 1 })
              } else {
                goToAuth()
              }
            }}
            variant="primary"
            className={classes.claimButton}
          >
            {lang[userLang].pages.btnTexts.claim}
          </AppButton>
          {!isOpenedFree && (
            <AppButton className={classes.shareButton} onClick={handleOpenCase}>
              <Icon view="moreBoxes" style={{ opacity: 0.7 }} />
              {btnTexts.openMore}
            </AppButton>
          )}
        </div>
      </div>
    </>
  )
}

export const RenderRewardContent = (props: { reward: RewardType }) => {
  const { reward } = props
  const userLang = useUnit($userLanguage)
  const { classes } = useStyles()
  const rewardDataAttribute = reward.rarity ? RewardRarity[reward.rarity] : ''

  const units = lang[userLang].pages.units
  const texts = lang[userLang].pages.boxPage.widgetTexts.rewardClaim

  const IS_TG_STARS_REWARD = reward.kind === 2
  const IS_SUBSCRIPTION_REWARD = reward.kind === 3
  const IS_YEARS = Number(reward.name) >= 12
  const icon: TIconVariant = reward.kind === 2 ? 'tgStar' : 'gamed'
  return (
    <div className={classes.textWrapper}>
      <div className={classes.amoutOfPointsWrapper}>
        <>
          <span className={classes.amountOfPoints}>
            {IS_TG_STARS_REWARD && texts.giftWorth} {''}
            {IS_SUBSCRIPTION_REWARD ? (
              <>
                {convertMonts(reward.name)} {IS_YEARS ? units.year : units.months}
              </>
            ) : (
              formatNumber(reward.name)
            )}
          </span>
          {!IS_SUBSCRIPTION_REWARD && reward.kind !== 0 && <Icon view={icon} width={32} />}
        </>
      </div>
      {reward.kind < 2 && (
        <span className={classes.rarityText} data-textcolor={rewardDataAttribute}>
          {''}
        </span>
      )}
    </div>
  )
}
